import React, { useMemo } from 'react';
import { View, ScrollView, Pressable, StyleSheet, Platform } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { pickHeadlineField, truncate } from '@/components/Tools/toolDetailHelpers';

// Detect a "file-shaped" value in an example: a single object with a preview
// URL (for thumbnail rendering) OR an array of such objects (multi-image).
function isFileLikeValue(v) {
  if (!v || typeof v !== 'object') return false;
  if (Array.isArray(v)) return v.length > 0 && v.every(isFileLikeValue);
  return !!(v.cdnPath || v.url || v.thumbnailPath || v.uri || v.mimeType || v._placeholder);
}
function getMediaKindFromValue(v) {
  if (Array.isArray(v)) return getMediaKindFromValue(v[0]);
  const mime = (v?.mimeType || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('video/')) return 'video';
  // Fall back to the field's hinted file type when present.
  if (v?.fileType === 'image') return 'image';
  if (v?.fileType === 'audio') return 'audio';
  if (v?.fileType === 'video') return 'video';
  return 'file';
}
function getPreviewUrlFromValue(v) {
  const o = Array.isArray(v) ? v[0] : v;
  return o?.thumbnailPath || o?.cdnPath || o?.url || o?.uri || null;
}
// Build a small list of media tiles to show at the top of the preset card.
function pickMediaPreviews(example, visibleFields) {
  const fileFieldNames = new Set(
    (visibleFields || [])
      .filter(f => f?.type === 'file' || ['file', 'image', 'multiImageUpload', 'imageMultiple', 'fileMultiple', 'audio', 'audioWaveform', 'video', 'videoTrimmer', 'imageMask'].includes(f?.ui?.component))
      .map(f => f.name)
  );
  const tiles = [];
  for (const [name, value] of Object.entries(example)) {
    if (!fileFieldNames.has(name)) continue;
    if (!isFileLikeValue(value)) continue;
    const items = Array.isArray(value) ? value : [value];
    for (const item of items.slice(0, 3)) {
      tiles.push({
        fieldName: name,
        kind: getMediaKindFromValue(item),
        url: getPreviewUrlFromValue(item),
        name: item?.name || null,
      });
    }
  }
  return tiles.slice(0, 3);
}

/**
 * Horizontally-scrollable strip of preset cards. Each card represents a saved
 * example for a tool — the user can tap to pre-fill the form with all of its
 * field values.
 */
function ToolExamplesPanel({ examples, visibleFields, palette, isRunning, onApply }) {
  const { colors } = useTheme();
  const { t } = useTranslation();

  // Derive a label map once so each card doesn't recompute it.
  const fieldLabelMap = useMemo(() => {
    const map = {};
    for (const f of visibleFields || []) {
      if (f?.name) map[f.name] = f?.ui?.displayName || f.name;
    }
    return map;
  }, [visibleFields]);

  const styles = useMemo(() => StyleSheet.create({
    scroll: {
      // Bleed-to-edge horizontal scroll: outer margin pushes the scroll past
      // the page padding; inner contentContainer re-applies that padding so
      // the first/last cards line up with everything else above.
      marginHorizontal: -24,
    },
    scrollContent: {
      flexDirection: 'row',
      // Force every card in the row to share the tallest card's height so
      // the body's `flex: 1` has something to grow into, pinning the CTA to
      // the bottom on shorter cards. Default is already `stretch` on RN, but
      // we set it explicitly to defend against the cross-axis quirks of
      // horizontal ScrollViews on web.
      alignItems: 'stretch',
      gap: 14,
      paddingHorizontal: 24,
      paddingVertical: 4,
    },
  }), []);

  if (!examples?.length) return null;

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={styles.scroll}
      contentContainerStyle={styles.scrollContent}
    >
      {examples.map((example, index) => (
        <PresetCard
          key={`preset-${index}`}
          index={index}
          example={example}
          visibleFields={visibleFields}
          fieldLabelMap={fieldLabelMap}
          palette={palette}
          colors={colors}
          isRunning={isRunning}
          onApply={onApply}
          t={t}
        />
      ))}
    </ScrollView>
  );
}

// ---------------------------------------------------------------------------
// PresetCard
//
// Shows the saved example as a cohesive card:
//   - A small numbered chip + the headline field as the card's title (so users
//     instantly recognize what the preset does instead of "Preset 1").
//   - Up to 3 secondary fields rendered as label/value rows with uppercase
//     micro-labels and clean truncated values.
//   - A footer CTA bar that lights up on hover ("Use this preset →").
// ---------------------------------------------------------------------------
function PresetCard({ index, example, visibleFields, fieldLabelMap, palette, colors, isRunning, onApply, t }) {
  // Pull file-shaped values out for a media strip, then build the headline
  // from the remaining text fields so we don't pick "[object Object]" as the
  // card title when the first example field happens to be an image.
  const mediaTiles = useMemo(() => pickMediaPreviews(example, visibleFields), [example, visibleFields]);
  const mediaFieldNames = useMemo(() => new Set(mediaTiles.map(m => m.fieldName)), [mediaTiles]);
  const exampleSansMedia = useMemo(
    () => Object.fromEntries(Object.entries(example).filter(([k]) => !mediaFieldNames.has(k))),
    [example, mediaFieldNames]
  );

  const headline = useMemo(() => pickHeadlineField(exampleSansMedia, visibleFields), [exampleSansMedia, visibleFields]);
  const secondaryEntries = useMemo(() => {
    const entries = Object.entries(exampleSansMedia);
    if (!headline) return entries.slice(0, 3);
    return entries.filter(([k]) => k !== headline.name).slice(0, 3);
  }, [exampleSansMedia, headline]);
  const remainingCount = Object.keys(exampleSansMedia).length - secondaryEntries.length - (headline ? 1 : 0);

  const styles = useMemo(() => StyleSheet.create({
    card: {
      // Horizontal scroll containers in RN stretch siblings on the cross axis
      // (vertical) to match the tallest card, BUT only when the card itself
      // is a flex item that fills the cross axis. RN-web's Pressable wraps
      // content in an extra div, which can collapse the cross-axis stretch
      // intermittently — so we belt-and-brace with `alignSelf: 'stretch'`
      // here AND `flex: 1` on body AND `marginTop: 'auto'` on the CTA,
      // guaranteeing the CTA always sits on the bottom edge regardless of
      // how the layout engine resolves this card's height.
      width: 300,
      alignSelf: 'stretch',
      flexDirection: 'column',
      borderRadius: 14,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      overflow: 'hidden',
      ...(Platform.OS === 'web' ? { transition: 'border-color 0.15s ease, transform 0.15s ease' } : null),
    },
    cardHover: {
      borderColor: palette.fg,
      ...(Platform.OS === 'web' ? { transform: [{ translateY: -2 }] } : null),
    },
    cardPressed: {
      opacity: 0.85,
    },
    body: {
      // Grow to absorb any extra vertical room so the CTA below is always
      // pinned to the bottom edge of the card, regardless of content length.
      flex: 1,
      padding: 16,
      gap: 14,
    },
    mediaStrip: {
      flexDirection: 'row',
      gap: 8,
    },
    mediaTile: {
      width: 64,
      height: 64,
      borderRadius: 10,
      overflow: 'hidden',
      backgroundColor: colors.solid_color_tertiary,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    mediaImage: {
      width: '100%',
      height: '100%',
    },
    mediaIconWrap: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
    },
    mediaBadge: {
      position: 'absolute',
      bottom: 4,
      right: 4,
      width: 18,
      height: 18,
      borderRadius: 9,
      backgroundColor: 'rgba(0,0,0,0.55)',
      alignItems: 'center',
      justifyContent: 'center',
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
    },
    numberPill: {
      width: 26,
      height: 26,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: palette.bg,
    },
    headlineWrap: {
      flex: 1,
      minWidth: 0,
      gap: 2,
    },
    microLabel: {
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    headlineText: {
      lineHeight: 20,
    },
    fieldsList: {
      gap: 10,
    },
    fieldRow: {
      gap: 2,
    },
    moreText: {
      marginTop: -2,
    },
    cta: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderTopWidth: 1,
      borderTopColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_tertiary,
      gap: 8,
      // `marginTop: auto` is the most reliable way to pin the CTA to the
      // bottom edge of the card. It works in both yoga (native) and the
      // web flexbox layout, and crucially does NOT depend on the parent
      // properly stretching the card's cross-axis size — if for any reason
      // the card ends up taller than its content (e.g. another card in the
      // strip is taller and the strip stretches all siblings to match),
      // this rule still does the right thing.
      marginTop: 'auto',
      ...(Platform.OS === 'web' ? { transition: 'background-color 0.15s ease' } : null),
    },
    ctaHover: {
      backgroundColor: palette.bg,
    },
    ctaIconBox: {
      width: 22,
      height: 22,
      borderRadius: 999,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: palette.bg,
      ...(Platform.OS === 'web' ? { transition: 'background-color 0.15s ease, transform 0.15s ease' } : null),
    },
    ctaIconBoxHover: {
      backgroundColor: palette.fg,
      ...(Platform.OS === 'web' ? { transform: [{ translateX: 2 }] } : null),
    },
  }), [colors, palette]);

  return (
    <Pressable
      onPress={() => onApply?.(example, index)}
      disabled={isRunning}
      accessibilityRole='button'
      accessibilityLabel={`Use preset ${index + 1}`}
      style={({ hovered, pressed }) => [
        styles.card,
        hovered && styles.cardHover,
        pressed && styles.cardPressed,
        isRunning && { opacity: 0.6 },
      ]}
    >
      {({ hovered }) => (
        <>
          <View style={styles.body}>
            <View style={styles.headerRow}>
              <View style={styles.numberPill}>
                <Text size='xs' weight='extraBold' style={{ color: palette.fg }}>{index + 1}</Text>
              </View>
              {headline && (
                <View style={styles.headlineWrap}>
                  <Text size='xxs' color='tertiary' weight='bold' style={styles.microLabel}>
                    {fieldLabelMap[headline.name] || headline.name}
                  </Text>
                  <Text size='m' weight='bold' color='primary' numberOfLines={2} style={styles.headlineText}>
                    {truncate(headline.value, 80)}
                  </Text>
                </View>
              )}
            </View>

            {mediaTiles.length > 0 && (
              <View style={styles.mediaStrip}>
                {mediaTiles.map((m, i) => (
                  <View key={`media-${i}`} style={styles.mediaTile}>
                    {m.kind === 'image' && m.url ? (
                      <Image
                        source={{ uri: m.url }}
                        style={styles.mediaImage}
                        contentFit='cover'
                      />
                    ) : (
                      <View style={styles.mediaIconWrap}>
                        <Icon
                          name={m.kind === 'audio' ? 'waveform' : m.kind === 'video' ? 'film' : m.kind === 'image' ? 'image' : 'paperclip'}
                          size={20}
                          color={colors.text_color_secondary}
                        />
                      </View>
                    )}
                    {m.kind !== 'image' && (
                      <View style={styles.mediaBadge}>
                        <Icon
                          name={m.kind === 'audio' ? 'volume' : m.kind === 'video' ? 'play' : 'paperclip'}
                          size={9}
                          color='#fff'
                        />
                      </View>
                    )}
                  </View>
                ))}
              </View>
            )}

            {secondaryEntries.length > 0 && (
              <View style={styles.fieldsList}>
                {secondaryEntries.map(([fieldName, value]) => (
                  <View key={fieldName} style={styles.fieldRow}>
                    <Text size='xxs' color='tertiary' weight='bold' style={styles.microLabel}>
                      {fieldLabelMap[fieldName] || fieldName}
                    </Text>
                    <Text size='s' color='primary' numberOfLines={2} style={{ lineHeight: 18 }}>
                      {truncate(value, 90)}
                    </Text>
                  </View>
                ))}
                {remainingCount > 0 && (
                  <Text size='xs' color='tertiary' style={styles.moreText}>
                    {(remainingCount === 1
                      ? (t?.('pages.tools.moreFieldsSingle') || '+ %COUNT% more field')
                      : (t?.('pages.tools.moreFields')      || '+ %COUNT% more fields')
                    ).replace('%COUNT%', remainingCount)}
                  </Text>
                )}
              </View>
            )}
          </View>

          <View style={[styles.cta, hovered && styles.ctaHover]}>
            <Text
              size='s'
              weight='bold'
              color={hovered ? 'primary' : 'secondary'}
            >
              {t?.('pages.tools.usePreset') || 'Use this preset'}
            </Text>
            <View style={[styles.ctaIconBox, hovered && styles.ctaIconBoxHover]}>
              <Icon name='arrow-right' size={11} color={hovered ? '#fff' : palette.fg} />
            </View>
          </View>
        </>
      )}
    </Pressable>
  );
}

export default React.memo(ToolExamplesPanel);
